import type { ItemCategoryReorderItemInput, ItemCategoryTreeNodeDto } from '@app/contracts';

export function cloneTree(nodes: ItemCategoryTreeNodeDto[]): ItemCategoryTreeNodeDto[] {
  return nodes.map((n) => ({
    category: { ...n.category },
    children: cloneTree(n.children),
  }));
}

export function findNode(
  nodes: ItemCategoryTreeNodeDto[],
  id: string,
): ItemCategoryTreeNodeDto | null {
  for (const n of nodes) {
    if (n.category.id === id) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
}

function subtreeContainsId(root: ItemCategoryTreeNodeDto, candidateId: string): boolean {
  if (root.category.id === candidateId) return true;
  return root.children.some((c) => subtreeContainsId(c, candidateId));
}

export function extractSubtree(
  nodes: ItemCategoryTreeNodeDto[],
  id: string,
): ItemCategoryTreeNodeDto | null {
  const idx = nodes.findIndex((n) => n.category.id === id);
  if (idx !== -1) {
    const [node] = nodes.splice(idx, 1);
    return node ?? null;
  }
  for (const n of nodes) {
    const found = extractSubtree(n.children, id);
    if (found) return found;
  }
  return null;
}

type Location = { parentId: string | null; list: ItemCategoryTreeNodeDto[]; index: number };

function findLocation(
  nodes: ItemCategoryTreeNodeDto[],
  id: string,
  parentId: string | null = null,
): Location | null {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!node) continue;
    if (node.category.id === id) {
      return { parentId, list: nodes, index: i };
    }
    const inner = findLocation(node.children, id, node.category.id);
    if (inner) return inner;
  }
  return null;
}

export function renumberOrderItems(nodes: ItemCategoryTreeNodeDto[]): void {
  nodes.forEach((n, i) => {
    n.category.orderItem = i + 1;
    renumberOrderItems(n.children);
  });
}

/**
 * Mutate `roots` (deep) pour refléter un déplacement. Retourne false si le dépôt est invalide.
 */
export function applyCategoryDrop(
  roots: ItemCategoryTreeNodeDto[],
  draggedId: string,
  targetId: string,
  dropZone: 'before' | 'after' | 'child',
): boolean {
  if (draggedId === targetId) return false;
  const draggedRoot = findNode(roots, draggedId);
  const targetNode = findNode(roots, targetId);
  if (!draggedRoot || !targetNode) return false;
  if (subtreeContainsId(draggedRoot, targetId)) return false;

  const removed = extractSubtree(roots, draggedId);
  if (!removed) return false;

  const loc = findLocation(roots, targetId, null);
  if (!loc) return false;

  let destList: ItemCategoryTreeNodeDto[];
  let insertIndex: number;

  if (dropZone === 'child') {
    const t = findNode(roots, targetId);
    if (!t) return false;
    destList = t.children;
    insertIndex = destList.length;
  } else {
    destList = loc.list;
    insertIndex = dropZone === 'before' ? loc.index : loc.index + 1;
  }

  destList.splice(insertIndex, 0, removed);
  renumberOrderItems(roots);
  return true;
}

export function flattenTreeOrder(
  nodes: ItemCategoryTreeNodeDto[],
  parentId: string | null = null,
): ItemCategoryReorderItemInput[] {
  const out: ItemCategoryReorderItemInput[] = [];
  for (const n of nodes) {
    out.push({ id: n.category.id, parentId, orderItem: n.category.orderItem });
    out.push(...flattenTreeOrder(n.children, n.category.id));
  }
  return out;
}

export function getNextOrderItem(
  nodes: ItemCategoryTreeNodeDto[],
  parentId: string | null,
): number {
  if (parentId === null) {
    if (nodes.length === 0) return 1;
    return Math.max(...nodes.map((n) => n.category.orderItem)) + 1;
  }
  const p = findNode(nodes, parentId);
  if (!p || p.children.length === 0) return 1;
  return Math.max(...p.children.map((c) => c.category.orderItem)) + 1;
}
